import { Injectable } from '@angular/core';
import { Observable, from, firstValueFrom, throwError } from 'rxjs';
import { timeout } from 'rxjs/operators';

import { MessstelleQrService } from '../messstelle-qr.service';
import { NetworkStatusService, TimeoutFehler } from './network-status.service';
import { OfflineDbService } from './offline-db.service';
import { OfflineSyncService } from './offline-sync.service';

import { neueUuid } from '../../Models/messstelle.model';
import { MessstelleQrInfo } from '../../Models/messstelle-qr.model';
import {
  DatenErgebnis,
  OfflineQrZuordnungEintrag,
  SchreibErgebnis,
  SyncQueueEintrag
} from '../../Models/offline.model';

/** Der QR-Stand einer Messstelle, wie ihn die Detailseite braucht. */
export interface QrStand {
  hatAktivenQr: boolean;
  token: string | null;

  /** True, wenn die Zuordnung offline erfasst wurde und noch nicht bestätigt ist. */
  wartend: boolean;
}

/**
 * Fehler aus der lokalen QR-Prüfung – mit demselben `code` wie die
 * Serverantworten, damit die Dialoge beide gleich behandeln können.
 */
export class QrZuordnungFehler extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = 'QrZuordnungFehler';
  }
}

/**
 * QR-Codes für Messstellen – online wie bisher über den Server, offline aus
 * dem lokalen Bestand.
 *
 * QR-Codes werden NIE hier erzeugt. Sie stammen aus einem vorab erzeugten
 * Pool (Sticker); vor Ort wird nur ein vorhandener Code gescannt und einer
 * Messstelle zugeordnet. Offline prüft der Dienst gegen alles, was lokal
 * bekannt ist: derselbe Token darf nicht zwei Messstellen gehören, und eine
 * Messstelle mit Code bekommt nur über „ersetzen" einen neuen. Die endgültige
 * Entscheidung trifft der Server beim Abgleich; weicht sie ab, entsteht ein
 * QR-Konflikt mit sicheren Auswegen – erzwingen lässt sich nichts.
 */
@Injectable({ providedIn: 'root' })
export class QrDataService {

  private static readonly LESE_TIMEOUT_MS = 12_000;
  private static readonly SCHREIB_TIMEOUT_MS = 15_000;

  constructor(
    private api: MessstelleQrService,
    private db: OfflineDbService,
    private network: NetworkStatusService,
    private sync: OfflineSyncService
  ) { }

  // ===================================================================
  // LESEN
  // ===================================================================

  /** Hat die Messstelle einen aktiven QR-Code? */
  getStand(messstelleId: number): Observable<DatenErgebnis<QrStand>> {
    return from(this.liesStand(messstelleId));
  }

  private async liesStand(messstelleId: number): Promise<DatenErgebnis<QrStand>> {
    const lokal = await this.db.ladeQrZuordnung(messstelleId);
    const offlineVerfuegbar = !!(await this.db.ladeDetail(messstelleId));

    // Eine wartende lokale Zuordnung ist maßgeblich – der Server kennt sie noch nicht.
    if (lokal?.quelle === 'offline') {
      return {
        daten: { hatAktivenQr: true, token: lokal.token, wartend: true },
        quelle: 'offline',
        standUtc: lokal.zugeordnetAmUtc
      };
    }

    if (this.network.lohntVersuch()) {
      try {
        const status = await this.mitTimeout(this.api.getQrStatus(messstelleId), QrDataService.LESE_TIMEOUT_MS);
        this.network.meldeErfolg();

        return { daten: { hatAktivenQr: status.hatAktivenQr, token: lokal?.token ?? null, wartend: false }, quelle: 'server' };
      } catch (fehler) {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler) || !offlineVerfuegbar) {
          throw fehler;
        }
      }
    }

    return {
      daten: { hatAktivenQr: !!lokal, token: lokal?.token ?? null, wartend: false },
      quelle: 'offline',
      standUtc: lokal?.zugeordnetAmUtc ?? null
    };
  }

  /** Der aktive QR-Code mit öffentlicher Adresse – für Anzeige und Druck. */
  getAktuellerQr(messstelleId: number): Observable<DatenErgebnis<MessstelleQrInfo>> {
    return from(this.liesAktuellenQr(messstelleId));
  }

  private async liesAktuellenQr(messstelleId: number): Promise<DatenErgebnis<MessstelleQrInfo>> {
    const lokal = await this.db.ladeQrZuordnung(messstelleId);

    if (lokal?.quelle === 'offline') {
      return { daten: this.alsInfo(lokal), quelle: 'offline', standUtc: lokal.zugeordnetAmUtc };
    }

    if (this.network.lohntVersuch()) {
      try {
        const info = await this.mitTimeout(this.api.getAktuellenQr(messstelleId), QrDataService.LESE_TIMEOUT_MS);
        this.network.meldeErfolg();
        await this.merkeServerstand(messstelleId, info.token);

        return { daten: info, quelle: 'server' };
      } catch (fehler) {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler) || !lokal) {
          throw fehler;
        }
      }
    }

    if (!lokal) {
      throw new Error('Dieser Messstelle ist offline kein QR-Code bekannt.');
    }

    return { daten: this.alsInfo(lokal), quelle: 'offline', standUtc: lokal.zugeordnetAmUtc };
  }

  // ===================================================================
  // SCHREIBEN
  // ===================================================================

  /**
   * Ordnet einen gescannten Code zu – oder ersetzt den vorhandenen.
   *
   * Online wie bisher; die Serverantworten (400/409 mit `code`) werden
   * unverändert durchgereicht. Ohne Verbindung wird lokal geprüft und
   * gespeichert, und die Zuordnung steht als `QR_ZUORDNEN` in der
   * Warteschlange.
   */
  zuordnen(messstelleId: number, wert: string, ersetzen: boolean): Observable<SchreibErgebnis<MessstelleQrInfo>> {
    return from(this.schreibeZuordnung(messstelleId, wert, ersetzen));
  }

  private async schreibeZuordnung(
    messstelleId: number,
    wert: string,
    ersetzen: boolean
  ): Promise<SchreibErgebnis<MessstelleQrInfo>> {
    if (this.network.lohntVersuch()) {
      try {
        const info = await this.mitTimeout(
          ersetzen ? this.api.ersetzen(messstelleId, wert) : this.api.zuordnen(messstelleId, wert),
          QrDataService.SCHREIB_TIMEOUT_MS
        );

        this.network.meldeErfolg();
        await this.merkeServerstand(messstelleId, info.token);

        return { daten: info, lokal: false };
      } catch (fehler) {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler)) {
          throw fehler;
        }
      }
    }

    return this.lokalZuordnen(messstelleId, wert, ersetzen);
  }

  private async lokalZuordnen(
    messstelleId: number,
    wert: string,
    ersetzen: boolean
  ): Promise<SchreibErgebnis<MessstelleQrInfo>> {
    if (!(await this.db.istVerfuegbar())) {
      throw new Error('Ohne Verbindung und ohne lokalen Speicher kann nicht gespeichert werden.');
    }

    const token = QrDataService.tokenAusScan(wert);

    if (!token) {
      throw new QrZuordnungFehler('invalid_qr', 'Der gescannte QR-Code ist ungültig.');
    }

    const detail = await this.db.ladeDetail(messstelleId);

    if (!detail) {
      throw new Error(
        'Diese Messstelle steht offline nicht zur Verfügung. '
        + 'Das Projekt muss vorher offline verfügbar gemacht werden.'
      );
    }

    const liste = await this.db.ladeListe(detail.messstellenListeId);
    const db = this.db.db;
    const jetzt = new Date().toISOString();

    let ergebnis!: OfflineQrZuordnungEintrag;

    await db.transaction('rw', db.qrZuordnungen, db.syncQueue, async () => {
      // ── Lokale Doppelvergabe-Sperre ──
      const andere = await db.qrZuordnungen.where('token').equals(token).first();

      if (andere && andere.messstelleId !== messstelleId) {
        throw new QrZuordnungFehler(
          'qr_already_assigned',
          'Dieser QR-Code ist nach den Daten auf diesem Gerät bereits einer anderen Messstelle zugeordnet.'
        );
      }

      const vorhanden = await db.qrZuordnungen.get(messstelleId);

      if (vorhanden && vorhanden.token === token) {
        throw new QrZuordnungFehler(
          'qr_already_assigned_to_this',
          'Dieser QR-Code ist dieser Messstelle bereits zugeordnet.'
        );
      }

      if (vorhanden && !ersetzen) {
        throw new QrZuordnungFehler(
          'messstelle_has_qr',
          'Für diese Messstelle ist bereits ein QR-Code vergeben. Bitte „QR-Code ersetzen“ verwenden.'
        );
      }

      // Der Serverstand VOR der Ersetzung – kommt zurück, falls die Zuordnung
      // beim Abgleich nicht möglich ist und zurückgenommen wird.
      const vorherigerToken =
        vorhanden?.quelle === 'server'
          ? vorhanden.token
          : (vorhanden?.vorherigerToken ?? null);

      ergebnis = {
        messstelleId,
        token,
        messstellenListeId: detail.messstellenListeId,
        projektId: liste?.projektId ?? null,
        quelle: 'offline',
        zugeordnetAmUtc: jetzt,
        vorherigerToken
      };

      await db.qrZuordnungen.put(ergebnis);

      // Eine noch wartende Zuordnung derselben Messstelle wird ersetzt –
      // der Sticker, der zuletzt geklebt wurde, zählt.
      const wartend = (await db.syncQueue
        .filter(e => e.typ === 'QR_ZUORDNEN' && e.messstelleId === messstelleId && !e.endgueltigFehlgeschlagen)
        .toArray())[0];

      const warteschlange: SyncQueueEintrag = {
        id: wartend?.id ?? neueUuid(),
        typ: 'QR_ZUORDNEN',
        entityId: messstelleId,
        clientEntityId: null,
        messstellenListeId: detail.messstellenListeId,
        messstelleId,
        projektId: liste?.projektId ?? null,
        performedAtUtc: jetzt,
        createdAtUtc: wartend?.createdAtUtc ?? jetzt,
        baseVersion: null,
        payload: { wert: token, ersetzen: ersetzen || !!vorherigerToken },
        retryCount: 0
      };

      await db.syncQueue.put(warteschlange);
    });

    await this.sync.aktualisiereStatus();

    if (this.network.lohntVersuch()) {
      void this.sync.synchronisiere('aenderung');
    }

    return { daten: this.alsInfo(ergebnis), lokal: true };
  }

  // ===================================================================
  // HILFSMITTEL
  // ===================================================================

  /**
   * Übernimmt den vom Server bestätigten Token in den lokalen Bestand –
   * nur für Messstellen, die offline gehalten werden, und nie über eine
   * wartende lokale Zuordnung hinweg.
   */
  private async merkeServerstand(messstelleId: number, token: string): Promise<void> {
    if (!(await this.db.istVerfuegbar())) {
      return;
    }

    const detail = await this.db.ladeDetail(messstelleId);

    if (!detail) {
      return;
    }

    const lokal = await this.db.ladeQrZuordnung(messstelleId);

    if (lokal?.quelle === 'offline') {
      return;
    }

    const liste = await this.db.ladeListe(detail.messstellenListeId);

    await this.db.db.qrZuordnungen.put({
      messstelleId,
      token,
      messstellenListeId: detail.messstellenListeId,
      projektId: liste?.projektId ?? null,
      quelle: 'server',
      zugeordnetAmUtc: new Date().toISOString(),
      vorherigerToken: null
    });
  }

  private alsInfo(eintrag: OfflineQrZuordnungEintrag): MessstelleQrInfo {
    return {
      id: 0,
      messstelleId: eintrag.messstelleId,
      token: eintrag.token,
      istAktiv: true,
      erstelltAmUtc: eintrag.zugeordnetAmUtc,
      erstelltVon: null,
      aktualisiertAmUtc: eintrag.zugeordnetAmUtc,
      publicUrl: QrDataService.publicUrl(eintrag.token)
    };
  }

  /** Dieselbe Adresse, die der Server auf den Sticker druckt – aus der eigenen Herkunft gebildet. */
  static publicUrl(token: string): string {
    const origin = typeof location !== 'undefined' ? location.origin : '';
    return `${origin}/q/m/${token}`;
  }

  /**
   * Liest den Token aus einem gescannten Wert – Adresse `…/q/m/{token}` oder
   * roher Token. Spiegelt `MessstelleQrToken.AusScan` im Backend.
   */
  static tokenAusScan(wert: string | null | undefined): string | null {
    const roh = (wert ?? '').trim();

    if (!roh) {
      return null;
    }

    let url: URL | null = null;

    try {
      url = new URL(roh);
    } catch {
      url = null;
    }

    if (url) {
      const teile = url.pathname.split('/').filter(t => t.length > 0);
      const index = teile.findIndex(t => t.toLowerCase() === 'm');

      return index >= 0 && index + 1 < teile.length ? teile[index + 1] : null;
    }

    return roh;
  }

  private mitTimeout<T>(quelle: Observable<T>, ms: number): Promise<T> {
    return firstValueFrom(
      quelle.pipe(
        timeout({
          each: ms,
          with: () => throwError(() => new TimeoutFehler(ms))
        })
      )
    );
  }

  private behandleFehler(fehler: unknown): void {
    if (NetworkStatusService.istNetzwerkfehler(fehler)) {
      this.network.meldeNetzwerkfehler();
    }
  }
}
